import { Grid, InputLabel, TextField, Typography } from "@mui/material";
import { fetchCustomerLocations } from "actions/customerActions";
import { CountrySelector } from "components/Common/CountrySelector";
import { DeliveryLocationSelector } from "components/Hardware/HardwareCheckout/DeliveryLocationSelector";
import { DeliveryOptions } from "components/Hardware/HardwareCheckout/DeliveryOptions";
import { getData } from "country-list";
import {
	selectCustomerDetails,
	selectCustomerLocations,
	selectHardwareDeliveryCountries,
} from "features/customer";
import { selectHardwarePurchaseType } from "features/hardware/hardwarePage";
import { mailDomainsSelectors } from "features/mailDomains";
import { usersSelectors } from "features/users";
import { useApiOnce, useAppSelector } from "hooks/hooks";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { CustomerLocation, Employee, FormEmployee, User } from "types";
import {
	DeliveryOption,
	HardwareDeliveryCountryOption,
	HardwarePurchaseType,
} from "utilities/constants/enums";
import { PersonalDetails } from "./PersonalDetails";
import { ShippingDetails } from "./ShippingDetails/ShippingDetails";
import styles from "./UserDetails.module.scss";

interface Props {
	employeeData: Employee & User & { companyName?: string };
	saveForm: (newData: any) => void;
	enableSaveButton?: () => void;
	setIsFormDataDirty?: (isDirty: boolean) => void;
	showShippingDetails?: boolean;
	isShippingAddressRequired?: boolean;
	isOnboarding?: boolean;
}

const UserDetails = ({
	employeeData,
	employeeData: {
		mailPrefix,
		mailSuffix,
		mail,
		givenName,
		surname,
		password,
		officeLocation,
		department,
		privateEmail,
		mobilePhone,
		country,
		employeeCountry,
		shippingCountry,
		streetAddress,
		postalCode,
		city,
		userPrincipalName,
		manager: managerId,
		hardwareNote,
		companyName,
	},
	saveForm,
	setIsFormDataDirty = () => {},
	showShippingDetails = true,
	isShippingAddressRequired = true,
	enableSaveButton = () => {},
	isOnboarding = true,
}: Props) => {
	const customerInfo = useAppSelector(selectCustomerDetails);
	const customerLocations = useAppSelector(selectCustomerLocations);
	const [selectedDeliveryLocation, setSelectedDeliveryLocation] =
		useState<CustomerLocation | null>(null);
	const hardwareDeliveryCountries = useAppSelector(selectHardwareDeliveryCountries);
	const hardwarePurchaseType = useAppSelector(selectHardwarePurchaseType);

	useApiOnce(fetchCustomerLocations, customerLocations);

	const getMailPrefix = () => {
		if (mailPrefix?.length > 0) {
			return mailPrefix;
		}
		const mailAddress = mail || userPrincipalName || "";
		return mailAddress.substring(0, mailAddress.indexOf("@"));
	};

	const getMailSuffix = () => {
		if (mailSuffix?.length > 0) {
			return mailSuffix;
		}
		const mailAddress = mail || userPrincipalName || "";
		return mailAddress.substring(mailAddress.indexOf("@") + 1);
	};

	const manager =
		useAppSelector((state) => usersSelectors.selectById(state, managerId)) ?? ({} as User);

	const suffix = getMailSuffix();
	const defaultValues = {
		mailPrefix: getMailPrefix(),
		mailSuffix: suffix,
		givenName,
		surname,
		password,
		officeLocation,
		department,
		manager,
		country,
		employeeCountry: employeeCountry ?? country,
		shippingCountry: shippingCountry ?? country,
		privateEmail,
		mobilePhone,
		streetAddress,
		postalCode,
		city,
		hardwareNote,
		deliveryOption: DeliveryOption.B2B,
	} as FormEmployee;

	const formProps = useForm<FieldValues>({
		mode: "onSubmit",
		defaultValues,
	});

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty },
	} = formProps;

	useEffect(() => {
		setIsFormDataDirty(isDirty);
	}, [isDirty, setIsFormDataDirty]);

	// Set the country field based on purchase type and hardware delivery countries
	useEffect(() => {
		if (hardwarePurchaseType === HardwarePurchaseType.Buy) {
			// When on Buy, set country based on hardwareDeliveryCountries
			if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Norway) {
				setValue("country", "Norway");
			} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Sweden) {
				setValue("country", "Sweden");
			} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Any) {
				// When delivery countries is "Any", default to Norway
				setValue("country", "Norway");
			}
		}
		// When on Lease, the country field will be set by the user or location selection
	}, [hardwarePurchaseType, hardwareDeliveryCountries, setValue]);

	// We need to ensure that the current mail suffix is also included in the available mail domain dropdown.
	// To avoid any issue where the user is not able to get the same mail address as before.
	const availableMailDomains = [...useAppSelector(mailDomainsSelectors.selectAll)];
	if (!availableMailDomains.some((domain) => domain.name === suffix)) {
		availableMailDomains.push({ name: suffix, id: 9876543210 });
	}

	// Handle location selection - this fills out the address fields
	const handleSelectDeliveryLocation = (location: CustomerLocation | null) => {
		setSelectedDeliveryLocation(location);

		if (!location) {
			// Reset the address fields if location is deselected
			setValue("streetAddress", "");
			setValue("postalCode", "");
			setValue("city", "");
			setValue("shippingCountry", "");
			// Reset country field based on purchase type
			if (hardwarePurchaseType === HardwarePurchaseType.Buy) {
				if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Norway) {
					setValue("country", "Norway");
				} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Sweden) {
					setValue("country", "Sweden");
				} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Any) {
					setValue("country", "Norway");
				}
			}
			return;
		}

		// Fill out the address fields with the selected location
		setValue("streetAddress", location.address);
		setValue("postalCode", location.postalCode);
		setValue("city", location.city);

		// Convert country code to country name if needed
		let countryName = location.country;
		if (location.countryCode && location.country === location.countryCode) {
			// If the country field contains the country code, convert it to country name
			const countryData = (getData as any)().find(
				(country: any) => country.code === location.countryCode,
			);
			if (countryData) {
				countryName = countryData.name;
			}
		}

		setValue("shippingCountry", countryName);
		// Also set the country field (used by location selector for filtering)
		setValue("country", countryName);
	};

	// Check if we should show the location selector
	// Show when hardware is selected and delivery option is B2B, OR when on Lease mode
	const deliveryOption = watch("deliveryOption");
	const shouldShowLocationSelector =
		isOnboarding &&
		(deliveryOption === DeliveryOption.B2B ||
			hardwarePurchaseType === HardwarePurchaseType.Lease);

	return (
		<form onSubmit={handleSubmit(saveForm)} id="personal-data-form" onChange={enableSaveButton}>
			<Grid container className={styles.container}>
				<PersonalDetails
					formProps={formProps}
					employeeData={{ ...employeeData, manager }}
					availableMailDomains={availableMailDomains}
				/>
				{showShippingDetails && (
					<Grid className={styles.shippingDetailsContainer}>
						<Typography variant="body1" fontWeight={500}>
							Address
						</Typography>
						{isOnboarding && (
							<>
								<Grid item py={1}>
									<DeliveryOptions control={formProps.control} />
								</Grid>
								{watch("deliveryOption") === DeliveryOption.B2B && (
									<Grid item>
										<InputLabel>
											<Typography variant="body1" display="inline">
												Company name
											</Typography>
										</InputLabel>
										<TextField
											fullWidth
											defaultValue={customerInfo?.departmentName ?? ""}
											value={companyName}
											size="small"
											InputProps={{
												classes: {
													input: styles.formTextInput,
												},
											}}
											{...register("companyName")}
										/>
									</Grid>
								)}
								{hardwarePurchaseType === HardwarePurchaseType.Lease && (
									<Grid item xs={6} mb={3}>
										<InputLabel>
											<Typography variant="body1" display="inline" mr={1}>
												Delivery country
											</Typography>
										</InputLabel>
										<CountrySelector
											value={watch("country")}
											register={register}
											setValue={setValue as any}
										/>
									</Grid>
								)}
							</>
						)}

						{/* Add location selector right before the address fields */}
						{shouldShowLocationSelector && (
							<Grid item mb={2}>
								<DeliveryLocationSelector
									selectedDeliveryCountry={watch("country")}
									selectedDeliveryLocation={selectedDeliveryLocation}
									handleSelectDeliveryLocation={handleSelectDeliveryLocation}
									hardwarePurchaseType={hardwarePurchaseType}
								/>
							</Grid>
						)}

						<Grid item mt={isOnboarding ? 0 : 1.8}>
							<ShippingDetails
								required={isShippingAddressRequired}
								isOnboarding={isOnboarding}
								formProps={formProps}
								xs={12}
							/>
						</Grid>
						{isOnboarding && (
							<Grid item xs={10}>
								<InputLabel>
									<Typography variant="body1" display="inline" mr={1}>
										Additional information
									</Typography>
									<Typography
										variant="body1"
										color="text.secondary"
										display="inline"
									>
										(optional)
									</Typography>
								</InputLabel>
								<TextField
									multiline
									placeholder="Notes will be shown inside the order history table"
									rows={3}
									fullWidth
									size="small"
									InputProps={{
										classes: {
											input: styles.formTextInput,
										},
									}}
									{...register("hardwareNote")}
								/>
							</Grid>
						)}
					</Grid>
				)}
			</Grid>
		</form>
	);
};

export { UserDetails };
